/**
 * MONEDA-PRESETS.JS — SSOT de presets de moneda (ISO 4217)
 *
 * Datos puros: definiciones estáticas de las 8 monedas soportadas.
 * Sin estado, sin lógica de formateo — solo datos.
 *
 * Consumido por: moneda-formateador.js, moneda-conversor.js, moneda-service.js
 * Compatible: ES Modules (fronts) + CommonJS (backend)
 */

// ============================================================
// PRESETS DE MONEDA (ISO 4217)
// ============================================================

export const PRESETS_MONEDA = {
    PEN: {
        codigo: 'PEN',
        nombre: 'Sol Peruano',
        simbolo: 'S/',
        decimales: 2,
        separadorDecimal: '.',
        separadorMiles: ',',
        posicion: 'left', // S/ 1,234.56
    },
    BRL: {
        codigo: 'BRL',
        nombre: 'Real Brasileño',
        simbolo: 'R$',
        decimales: 2,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'left', // R$ 1.234,56
    },
    COP: {
        codigo: 'COP',
        nombre: 'Peso Colombiano',
        simbolo: '$',
        decimales: 0,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'left', // $ 1.234
    },
    ARS: {
        codigo: 'ARS',
        nombre: 'Peso Argentino',
        simbolo: '$',
        decimales: 2,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'left', // $ 1.234,56
    },
    CLP: {
        codigo: 'CLP',
        nombre: 'Peso Chileno',
        simbolo: '$',
        decimales: 0,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'left', // $ 1.234
    },
    USD: {
        codigo: 'USD',
        nombre: 'Dólar Estadounidense',
        simbolo: '$',
        decimales: 2,
        separadorDecimal: '.',
        separadorMiles: ',',
        posicion: 'left', // $ 1,234.56
    },
    MXN: {
        codigo: 'MXN',
        nombre: 'Peso Mexicano',
        simbolo: '$',
        decimales: 2,
        separadorDecimal: '.',
        separadorMiles: ',',
        posicion: 'left', // $ 1,234.56
    },
    EUR: {
        codigo: 'EUR',
        nombre: 'Euro',
        simbolo: '€',
        decimales: 2,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'right', // 1.234,56 €
    },
    PYG: {
        codigo: 'PYG',
        nombre: 'Guaraní Paraguayo',
        simbolo: '₲',
        decimales: 0,
        separadorDecimal: ',',
        separadorMiles: '.',
        posicion: 'left', // ₲ 1.234
    },
};

// Orden para mostrar en <select> y dropdowns
export const ORDEN_MONEDAS_PARA_SELECTOR = ['PEN', 'BRL', 'COP', 'ARS', 'CLP', 'PYG', 'MXN', 'USD', 'EUR'];

// Moneda fallback cuando no hay configuración
export const MONEDA_POR_DEFECTO = 'PEN';

// ============================================================
// FUNCIONES DE ACCESO
// ============================================================

/**
 * Obtiene el preset de una moneda por su código ISO
 * @param {string} codigoIso - Código ISO 4217 (ej: 'BRL', 'PEN')
 * @returns {object} Preset completo. Fallback a PEN si no existe
 */
export function obtenerPresetPorCodigo(codigoIso) {
    return PRESETS_MONEDA[codigoIso] || PRESETS_MONEDA[MONEDA_POR_DEFECTO];
}

/**
 * Unidades menores por unidad de moneda (SSOT del factor unidad↔unidad-menor).
 * Monedas con decimales=0 (PYG, COP) almacenan unidades directas: factor 1.
 * Con 2 decimales (BRL, PEN): 100 centavos por unidad.
 * @param {string|object} moneda - Código ISO o preset object
 * @returns {number} Ej: BRL → 100, PYG → 1
 */
export function unidadesMenoresPorUnidad(moneda) {
    const preset =
        typeof moneda === 'object' && moneda !== null ? moneda : obtenerPresetPorCodigo(moneda || MONEDA_POR_DEFECTO);
    return preset.decimales > 0 ? Math.pow(10, preset.decimales) : 1;
}

/**
 * SSOT de ENTRADA de montos: convierte unidades display (lo que tipea un humano,
 * ej. "150.50") a entero en unidad menor de la moneda (BRL/PEN ×100; PYG/COP ×1).
 * NUNCA hacer Math.round(x * 100) a mano — corrompe monedas de 0 decimales.
 * @param {number|string} unidades - Monto en unidades display
 * @param {string|object} moneda - Código ISO o preset object
 * @returns {number} Entero en unidad menor
 */
export function convertirUnidadesAUnidadMenor(unidades, moneda) {
    return Math.round((parseFloat(unidades) || 0) * unidadesMenoresPorUnidad(moneda));
}

/**
 * Retorna todos los presets en orden para poblar <select>
 * @returns {Array<{codigo, nombre, simbolo, decimales, separadorDecimal, separadorMiles, posicion}>}
 */
export function obtenerTodosLosPresetsEnOrden() {
    const resultado = [];
    for (let i = 0; i < ORDEN_MONEDAS_PARA_SELECTOR.length; i++) {
        resultado.push(PRESETS_MONEDA[ORDEN_MONEDAS_PARA_SELECTOR[i]]);
    }
    return resultado;
}

/**
 * Verifica si un código ISO corresponde a una moneda soportada
 * @param {string} codigoIso
 * @returns {boolean}
 */
export function existeCodigoDeMoneda(codigoIso) {
    return codigoIso in PRESETS_MONEDA;
}

// ============================================================
// COMMONJS COMPATIBILITY
// ============================================================

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        PRESETS_MONEDA,
        ORDEN_MONEDAS_PARA_SELECTOR,
        MONEDA_POR_DEFECTO,
        obtenerPresetPorCodigo,
        obtenerTodosLosPresetsEnOrden,
        existeCodigoDeMoneda,
    };
}
